<script setup>
import DropdownOption from "@/components/recommendationFeedbacks/DropdownOption";
import { FEEDBACK_CONFIGS } from "@/components/recommendationFeedbacks/lib/feedbackConfigs";

defineProps({
  type: {
    type: String,
    default: null, // 'filter', 'similarity', or null
  },
  currentFeedbackType: {
    type: String,
    default: null,
  },
  availableOptions: {
    type: Array,
    required: true,
  },
  showRemove: {
    type: Boolean,
    default: false,
  },
  removeDisabled: {
    type: Boolean,
    default: false,
  },
  removeTooltip: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["option-click", "remove-click"]);

function onOptionClick(option) {
  emit("option-click", option);
}

function onRemoveClick() {
  emit("remove-click");
}
</script>

<template>
  <div class="dropdown-menu-container">
    <template v-if="availableOptions.length > 0">
      <div class="value-dropdown-description">
        <span v-if="type === 'similarity'">
          We've suggested this attribute as <b>similar</b> to your job criteria. <br><br>Mark it as a:
        </span>
        <span v-else-if="currentFeedbackType === 'positive_filter'">
          This attribute is part of this job's <b>hard criteria</b>. <br><br>We will only show you candidates that contain this attribute. <br><br>Change it to:
        </span>
        <span v-else-if="currentFeedbackType === 'positive_recent_employments_filter'">
          This attribute is part of this job's <b>hard criteria</b>.<br><br> Candidates must have applicable keywords in the past 5 years<br> and in their most recent three employments. <br>We will only show you candidates that contain this attribute.<br><br>Change it to:
        </span>
        <span v-else-if="currentFeedbackType === 'positive'">
           This attribute is part of this job's <b>soft criteria</b>. <br><br>We rank candidates higher if they have more <b>soft criteria</b> attributes, but this doesn't exclude those who lack them.<br><br>Change it to:
        </span>
        <span v-else-if="currentFeedbackType === 'negative_filter'">
          For this job, this attribute is a <b>disqualifier</b>. <br><br>Candidate profiles containing this attribute will be excluded. <br><br>Change it to:
        </span>
        <span v-else>
          For this job, this attribute is a:
        </span>
      </div>

      <el-dropdown-item
        v-for="(option, index) in availableOptions"
        :key="option"
        class="dropdown-item"
        :divided="index === 0"
      >
        <DropdownOption
          :type="option"
          @click="onOptionClick(option)"
        >
          {{ FEEDBACK_CONFIGS.find(fc => fc.feedback === option)?.dropdownLabel || FEEDBACK_CONFIGS.find(fc => fc.feedback === option)?.label }}
        </DropdownOption>
      </el-dropdown-item>
    </template>

    <el-dropdown-item v-if="showRemove" class="dropdown-item" :divided="availableOptions.length > 0">
      <el-tooltip :disabled="!removeDisabled" :content="removeTooltip">
        <DropdownOption :disabled="removeDisabled" @click="onRemoveClick">
          Remove
        </DropdownOption>
      </el-tooltip>
    </el-dropdown-item>
  </div>
</template>

<style scoped lang="scss">
.dropdown-item {
  padding: 0;

  &:hover {
    background-color: transparent !important;
  }
}

.dropdown-menu-container {
  padding-left: 8px;
  padding-right: 8px;
  overflow: hidden;

  .value-dropdown-description {
    padding: 0 16px;
    color: $grey-500;
    font-size: 12px;
    font-weight: 500;
    max-width: 330px;
  }
}
</style>
